import {
  type AnyRecord,
  CONFIDENCE_LEVELS,
  DIALOGUE_EVENT_TYPES,
  ENGAGEMENT_STATUSES,
  INVOLVEMENT_TYPES,
  type RecordApiKind,
  SEVERITIES,
  THEMES,
} from '@fruuit/shared'
import { StatusBadge } from '@fruuit/ui'
import type { ReactNode } from 'react'
import { formatDate, vocabLabel } from './format'
import { RecordName } from './RecordPicker'

interface Row {
  label: string
  value: ReactNode
  /** Long text spans the full width. */
  wide?: boolean
}

function refs(kind: RecordApiKind, ids: readonly string[]): ReactNode {
  if (ids.length === 0) return <span className="text-sage">None linked</span>
  return (
    <ul className="flex flex-col gap-1">
      {ids.map((id) => (
        <li key={id} className="text-sm">
          <RecordName kind={kind} id={id} />
        </li>
      ))}
    </ul>
  )
}

function person(people: Record<string, string> | undefined, id: string | null): ReactNode {
  if (!id) return '—'
  return people?.[id] ?? id
}

/** The fields a reviewer reads before acting (S14 "Read full record"). */
export function summaryRows(record: AnyRecord, people?: Record<string, string>): Row[] {
  switch (record.kind) {
    case 'assessments':
      return [
        { label: 'Company', value: record.issuer_name ?? record.issuer_id },
        { label: 'Conclusion', value: <StatusBadge status={record.finding} /> },
        { label: 'Priority', value: <StatusBadge status={record.priority} /> },
        { label: 'Involvement', value: vocabLabel(INVOLVEMENT_TYPES, record.involvement) },
        { label: 'Severity', value: vocabLabel(SEVERITIES, record.severity) },
        { label: 'Confidence', value: vocabLabel(CONFIDENCE_LEVELS, record.confidence) },
        { label: 'Effective date', value: formatDate(record.effective_date) },
        { label: 'Norms', value: record.norm, wide: true },
        { label: 'Rationale', value: record.rationale, wide: true },
        { label: 'Remediation', value: record.remediation, wide: true },
        { label: 'Reason for this version', value: record.change_reason ?? '—', wide: true },
        { label: 'Incidents', value: refs('incidents', record.incident_ids), wide: true },
        { label: 'Sources', value: refs('sources', record.source_ids), wide: true },
      ]
    case 'milestones':
      return [
        { label: 'Case', value: record.engagement_title ?? record.engagement_id },
        { label: 'Objective', value: record.objective_title ?? record.objective_id },
        { label: 'Stage', value: <StatusBadge status={record.stage} label={record.stage} /> },
        { label: 'Assessment date', value: formatDate(record.assessment_date) },
        { label: 'Rationale', value: record.rationale, wide: true },
        { label: 'Limitations', value: record.limitations, wide: true },
        { label: 'Evidence', value: refs('sources', record.evidence_ids), wide: true },
      ]
    case 'dialogues':
      return [
        { label: 'Case', value: record.engagement_title ?? record.engagement_id },
        { label: 'Type', value: vocabLabel(DIALOGUE_EVENT_TYPES, record.event_type) },
        { label: 'Date', value: formatDate(record.event_date) },
        { label: 'Substantive', value: record.substantive ? 'Yes' : 'No' },
        { label: 'Company response', value: record.company_response, wide: true },
        { label: "Fruuit's judgment", value: record.fruuit_judgment, wide: true },
        {
          label: 'Next action',
          value: `${record.next_action} · due ${formatDate(record.due_date)}`,
          wide: true,
        },
        { label: 'Sources', value: refs('sources', record.source_ids), wide: true },
      ]
    case 'engagements':
      return [
        { label: 'Company', value: record.issuer_name ?? record.issuer_id },
        {
          label: 'Status',
          value: (
            <StatusBadge
              status={record.status}
              label={vocabLabel(ENGAGEMENT_STATUSES, record.status)}
            />
          ),
        },
        {
          label: 'Theme',
          value: record.theme ? vocabLabel(THEMES, record.theme) : 'No theme recorded',
        },
        { label: 'Manager', value: person(people, record.assigned_manager_id) },
        { label: 'Case reviewer', value: person(people, record.assigned_reviewer_id) },
        { label: 'Next action due', value: formatDate(record.due_date) },
        { label: 'Next action', value: record.next_action, wide: true },
        {
          label: 'Last released',
          value: record.released?.released_at
            ? `${formatDate(record.released.released_at)} by ${person(people, record.released.released_by)}`
            : 'Never released',
        },
      ]
    case 'objectives':
      return [
        { label: 'Case', value: record.engagement_title ?? record.engagement_id },
        { label: 'Owner', value: person(people, record.owner_id) },
        { label: 'Due', value: formatDate(record.due_date) },
        { label: 'Baseline', value: record.baseline, wide: true },
        { label: 'The change we seek', value: record.change_request, wide: true },
        { label: 'Next action', value: record.next_action, wide: true },
        {
          label: 'Last released',
          value: record.released?.released_at
            ? `${formatDate(record.released.released_at)} by ${person(people, record.released.released_by)}`
            : 'Never released',
        },
      ]
    case 'sources':
      return [
        { label: 'Source class', value: record.source_class },
        {
          label: 'Sharing',
          value: record.sharing === 'client' ? 'Shareable with clients' : 'Internal to Fruuit',
        },
        { label: 'Retrieved', value: formatDate(record.retrieved_at) },
        {
          label: 'Published',
          value: record.publication_date ? formatDate(record.publication_date) : 'Unverified',
        },
        { label: 'Summary', value: record.summary, wide: true },
      ]
    case 'incidents':
      return [
        { label: 'Company', value: record.issuer_name ?? record.issuer_id },
        { label: 'Location', value: record.location },
        { label: 'Legal status', value: record.legal_status },
        {
          label: 'Event date',
          value: record.event_date ? formatDate(record.event_date) : 'Unverified',
        },
        { label: 'Description', value: record.description, wide: true },
        { label: 'Company response', value: record.company_response, wide: true },
      ]
    case 'issuers':
      return [
        { label: 'Country', value: record.country },
        { label: 'Sector', value: record.sector },
      ]
    case 'securities':
      return [
        { label: 'Identifier', value: `${record.identifier_type} ${record.identifier_value}` },
        { label: 'Mapping', value: <StatusBadge status={record.mapping_state} /> },
        { label: 'Issuer', value: record.issuer_name ?? '—' },
      ]
  }
}

export function RecordSummary({
  record,
  people,
}: {
  record: AnyRecord
  people?: Record<string, string>
}) {
  const rows = summaryRows(record, people)
  return (
    <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
      {rows.map((row) => (
        <div key={row.label} className={row.wide ? 'sm:col-span-2' : undefined}>
          <dt className="text-xs font-semibold tracking-[0.06em] text-sage uppercase">
            {row.label}
          </dt>
          <dd className="mt-1 text-sm leading-relaxed whitespace-pre-line text-ink">{row.value}</dd>
        </div>
      ))}
    </dl>
  )
}
